import ControlNumberPaymentForm from "./mobile-money-payment-form";
import { PaymentCategory } from "@/features/applications/repositories/licences-repository.ts";
import type { ZanCustomerFields } from "@/features/applications/services/applications-services";

interface IProps extends ZanCustomerFields {
    header: string;
    onSubmit?: () => void;
    inspectionFee?: string;
    paymentCategory: PaymentCategory;
    onPaymentSuccess?: () => void;
    baseFee?: string;
    managementFee?: string;
    totalFees?: string;
    defaultPhoneNumber?: string;
}

export default function PaymentMethodsForms({
    header,
    onSubmit,
    inspectionFee,
    paymentCategory,
    onPaymentSuccess,
    baseFee,
    managementFee,
    totalFees,
    defaultPhoneNumber,
    ...customerFields
}: IProps) {
    const total = totalFees ?? inspectionFee ?? baseFee ?? '0';
    const fmt = (v?: string) => v ? parseInt(v).toLocaleString() : null;

    return (
        <div className="flex flex-col gap-0">
            {/* Header */}
            <div className="px-6 pt-2 pb-5 border-b border-gray-100">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-1">{header}</p>
                <p className="text-3xl font-bold text-gray-900">
                    {parseInt(total).toLocaleString()}
                    <span className="text-base font-medium text-gray-400 ml-1.5">TZS</span>
                </p>
            </div>

            {/* Applicants generate a control number and pay it through their bank or mobile
                money provider. Direct mobile-push and card payment were removed from the
                customer portal: settlement is handled entirely through the control number. */}
            <div className="px-6 pt-5 pb-1 min-h-[200px]">
                <ControlNumberPaymentForm
                    paymentCategory={paymentCategory}
                    totalFees={total}
                    onPaymentSuccess={onPaymentSuccess}
                    onSubmit={onSubmit}
                    defaultPhoneNumber={defaultPhoneNumber}
                    {...customerFields}
                />
            </div>

            {/* Fee breakdown */}
            <div className="mx-6 mb-6 mt-4 border border-gray-100 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Fee Breakdown</p>
                </div>
                <div className="px-4 py-3 space-y-2">
                    {baseFee && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Base Fee</span>
                            <span className="font-medium text-gray-800">{fmt(baseFee)} TZS</span>
                        </div>
                    )}
                    {managementFee && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Management Fee</span>
                            <span className="font-medium text-gray-800">{fmt(managementFee)} TZS</span>
                        </div>
                    )}
                    {inspectionFee && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Inspection Fee</span>
                            <span className="font-medium text-gray-800">{fmt(inspectionFee)} TZS</span>
                        </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-gray-100">
                        <span className="font-semibold text-gray-900 text-sm">Total</span>
                        <span className="font-bold text-gray-900">{fmt(total)} TZS</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
